// templateBannerUtils.ts
// Utilities for merging live StayUpToDate banner rows with saved template_settings banners.

import {
  getCoverScale,
  loadImage,
  PROFILE_CROP,
} from "../../../Utils/imageCrop/cropImageFile";
import type { BannerItem, CreatorData } from "./types";

type TemplateBanners = NonNullable<Partial<CreatorData>["banners"]>;

/**
 * Prefer live StayUpToDate banner rows, but fill empty `image` from saved
 * `template_settings.banners` (fixes hero preview when live `details` omits image).
 */
export function mergeTemplateBanners(
  live: TemplateBanners | undefined,
  tmpl: TemplateBanners | undefined,
): TemplateBanners | undefined {
  if (!live && !tmpl) return undefined;
  if (!live) return tmpl;
  if (!tmpl) return live;
  const keys = ["hero", "promo", "footer"] as const;
  const liveRids = new Set(
    [
      ...keys.map((k) => String(live[k]?.stayComponentRowId || "").trim()),
      ...(live.catalog || []).map((item) =>
        String(item?.stayComponentRowId || "").trim(),
      ),
    ].filter(Boolean),
  );
  const out: TemplateBanners = { ...live };
  for (const k of keys) {
    const L = live[k];
    const T = tmpl[k];
    if (!L && !T) {
      delete (out as Record<string, unknown>)[k];
      continue;
    }
    if (!L) {
      (out as Record<string, unknown>)[k] = T;
      continue;
    }
    if (!T) {
      (out as Record<string, unknown>)[k] = L;
      continue;
    }
    const liveRid = String(L.stayComponentRowId || "").trim();
    const tmplRid = String(T.stayComponentRowId || "").trim();
    // Keep a just-seeded Add until the live strip includes that row — otherwise
    // order-based live hero/promo briefly repaints the previous banner.
    if (
      tmplRid &&
      tmplRid !== liveRid &&
      String(T.image || "").trim() &&
      !liveRids.has(tmplRid)
    ) {
      out[k] = T;
      continue;
    }
    const img =
      String(L.image || "").trim() || String(T.image || "").trim();
    out[k] = {
      ...T,
      ...L,
      image: img,
      stayComponentRowId: L.stayComponentRowId ?? T.stayComponentRowId,
    };
  }
  const catalogOrder: string[] = [];
  const catalogByRid = new Map<string, BannerItem>();
  // Live rows win on identity, but a just-seeded upload must survive when the
  // refetch's `details` image is still empty.
  for (const item of [...(live.catalog || []), ...(tmpl.catalog || [])]) {
    const rid = String(item?.stayComponentRowId || item?.id || "").trim();
    if (!rid) continue;
    const prev = catalogByRid.get(rid);
    if (!prev) {
      catalogOrder.push(rid);
      catalogByRid.set(rid, item);
      continue;
    }
    const image =
      String(prev.image || "").trim() || String(item.image || "").trim();
    catalogByRid.set(rid, { ...item, ...prev, image });
  }
  const catalog = catalogOrder
    .map((rid) => catalogByRid.get(rid))
    .filter((item): item is BannerItem => Boolean(item));
  if (catalog.length) out.catalog = catalog;
  return out;
}

export type BannerSlot = "hero" | "promo" | "footer";

export function bannerSlotForSectionId(sectionId?: string): BannerSlot {
  if (sectionId === "promoBanner") return "promo";
  if (sectionId === "footer") return "footer";
  return "hero";
}

/** Prefer the Stay row bound on the section, then the built-in hero/promo/footer slot. */
export function resolveWebsiteBanner(
  banners: CreatorData["banners"] | undefined,
  cfg?: unknown,
  sectionId?: string,
): { slot: BannerSlot; banner: BannerItem | undefined } {
  const cid = String(
    cfg && typeof cfg === "object" && "componentId" in cfg
      ? (cfg as { componentId?: unknown }).componentId
      : "",
  ).trim();
  const slots: BannerSlot[] = ["hero", "promo", "footer"];
  const slot = bannerSlotForSectionId(sectionId);
  if (cid && banners) {
    let slotHit: { slot: BannerSlot; banner: BannerItem } | undefined;
    for (const s of slots) {
      const item = banners[s];
      if (String(item?.stayComponentRowId || "").trim() === cid) {
        slotHit = { slot: s, banner: item };
        break;
      }
    }
    const fromCatalog = (banners.catalog || []).find(
      (item) => String(item?.stayComponentRowId || item?.id || "").trim() === cid,
    );
    if (slotHit) {
      const image =
        String(slotHit.banner.image || "").trim() ||
        String(fromCatalog?.image || "").trim();
      return {
        slot: slotHit.slot,
        banner:
          image && image !== String(slotHit.banner.image || "").trim()
            ? { ...slotHit.banner, image }
            : slotHit.banner,
      };
    }
    if (fromCatalog) return { slot, banner: fromCatalog };
    // Bound id isn't loaded yet. Do not fall back to another banner's copy.
    return { slot, banner: undefined };
  }
  return { slot, banner: banners?.[slot] };
}

export function websiteBannerContentPaths(
  slot: BannerSlot,
  banner?: { stayComponentRowId?: string } | null,
) {
  const rid = String(banner?.stayComponentRowId || "").trim();
  if (rid) {
    return {
      title: `sutc.${rid}.name`,
      subtitle: `sutc.${rid}.subtitle`,
      ctaLink: `sutc.${rid}.ctaLink`,
      imgSlot: `sutc-${rid}-detail-image`,
    };
  }
  return {
    title: `banner.${slot}.title`,
    subtitle: `banner.${slot}.subtitle`,
    ctaLink: `banner.${slot}.cta`,
    imgSlot: `banner-${slot}-image`,
  };
}

export type BannerImageFrame = {
  zoom: number;
  x: number;
  y: number;
};

export const BANNER_IMAGE_ZOOM_MIN = 1;
export const BANNER_IMAGE_ZOOM_MAX = 2.5;

export function normalizeBannerImageFrame(raw?: {
  imageZoom?: unknown;
  imageX?: unknown;
  imageY?: unknown;
  zoom?: unknown;
  x?: unknown;
  y?: unknown;
} | null): BannerImageFrame {
  const zoom = Number(raw?.imageZoom ?? raw?.zoom);
  const x = Number(raw?.imageX ?? raw?.x);
  const y = Number(raw?.imageY ?? raw?.y);
  return {
    zoom:
      Number.isFinite(zoom) && zoom >= BANNER_IMAGE_ZOOM_MIN
        ? Math.min(BANNER_IMAGE_ZOOM_MAX, zoom)
        : 1,
    x: Number.isFinite(x) ? Math.min(100, Math.max(0, x)) : 50,
    y: Number.isFinite(y) ? Math.min(100, Math.max(0, y)) : 50,
  };
}

/** Section cfg wins once the creator has positioned the cover; otherwise the saved banner row. */
export function resolveBannerImageFrame(
  cfg?: {
    imageZoom?: unknown;
    imageX?: unknown;
    imageY?: unknown;
  } | null,
  banner?: {
    imageZoom?: unknown;
    imageX?: unknown;
    imageY?: unknown;
  } | null,
): BannerImageFrame {
  const hasCfg =
    cfg != null &&
    (cfg.imageZoom != null || cfg.imageX != null || cfg.imageY != null);
  return normalizeBannerImageFrame(hasCfg ? cfg : banner);
}

export function bannerImageFrameCfg(frame: BannerImageFrame): {
  imageZoom: number;
  imageX: number;
  imageY: number;
} {
  const next = normalizeBannerImageFrame(frame);
  return { imageZoom: next.zoom, imageX: next.x, imageY: next.y };
}

/**
 * Cover-frame focal point for My Links thumbs (`user_links.image_crop.coverFrame`).
 * Kept separate from line/grid cropper entries so classic crop + website grab can coexist.
 */
export function readLinkCoverFrame(imageCrop: unknown): BannerImageFrame | null {
  if (!imageCrop || typeof imageCrop !== "object" || Array.isArray(imageCrop)) {
    return null;
  }
  const cover = (imageCrop as Record<string, unknown>).coverFrame;
  if (!cover || typeof cover !== "object") return null;
  const next = normalizeBannerImageFrame(cover as BannerImageFrame);
  if (next.zoom === 1 && next.x === 50 && next.y === 50) return null;
  return next;
}

/**
 * Merge cover zoom into `image_crop.coverFrame`.
 * Link thumbs keep `writeCover: false` (default) — CSS on an already
 * pixel-cropped display URL double-zooms. LiB profile passes `writeCover: true`.
 */
export function mergeLinkCoverFrame(
  imageCrop: unknown,
  frame: BannerImageFrame | null,
  opts?: { writeCover?: boolean },
): Record<string, unknown> | null {
  const base =
    imageCrop && typeof imageCrop === "object" && !Array.isArray(imageCrop)
      ? { ...(imageCrop as Record<string, unknown>) }
      : {};
  if (!frame) {
    delete base.coverFrame;
    return Object.keys(base).length ? base : null;
  }
  if (!opts?.writeCover) return base;
  const next = normalizeBannerImageFrame(frame);
  if (next.zoom === 1 && next.x === 50 && next.y === 50) {
    delete base.coverFrame;
    return Object.keys(base).length ? base : null;
  }
  base.coverFrame = bannerImageFrameCfg(next);
  return base;
}

/** Content-edit key for a template image slot. Empty means “leave the image as-is”. */
export function imageFrameEditKey(slot: string): string {
  return `imageFrame.${slot}`;
}

export function serializeImageFrameEdit(frame: BannerImageFrame): string {
  return JSON.stringify(bannerImageFrameCfg(frame));
}

/** Null when nothing was saved, so existing images keep their current crop. */
export function parseImageFrameEdit(raw: string): BannerImageFrame | null {
  const text = String(raw || "").trim();
  if (!text) return null;
  try {
    const value = JSON.parse(text) as unknown;
    if (!value || typeof value !== "object") return null;
    return normalizeBannerImageFrame(value as BannerImageFrame);
  } catch {
    return null;
  }
}

/** Undefined when the cover is the default center fit, so existing banners stay untouched. */
export function bannerImageFrameStyle(
  frame: BannerImageFrame,
  opts?: { objectFit?: "cover" | "contain" },
):
  | {
      objectFit: "cover" | "contain";
      objectPosition: string;
      transform: string;
      transformOrigin: string;
    }
  | undefined {
  const next = normalizeBannerImageFrame(frame);
  if (next.zoom === 1 && next.x === 50 && next.y === 50) return undefined;
  return {
    // Default cover keeps circular avatars / legacy thumbs intact.
    // Banner + gallery uploads pass `contain` so the full image stays visible.
    objectFit: opts?.objectFit ?? "cover",
    objectPosition: `${next.x}% ${next.y}%`,
    transform: `scale(${next.zoom})`,
    transformOrigin: `${next.x}% ${next.y}%`,
  };
}

/**
 * Bake CSS cover + object-position + scale (BannerImageFrameControl) into a
 * square PNG so `user.picture` can store the framed result (account chip, etc.).
 */
export async function exportBannerImageFrameToFile(args: {
  imageUrl: string;
  frame: BannerImageFrame;
  fileName?: string;
  outputSize?: number;
}): Promise<File> {
  const frame = normalizeBannerImageFrame(args.frame);
  const outputSize = Math.max(
    1,
    Math.round(args.outputSize || PROFILE_CROP.outputWidth),
  );
  let revoke: (() => void) | undefined;
  let imageSrc = args.imageUrl;
  if (!imageSrc.startsWith("blob:") && !imageSrc.startsWith("data:")) {
    try {
      const response = await fetch(imageSrc, { mode: "cors" });
      if (response.ok) {
        const objectUrl = URL.createObjectURL(await response.blob());
        imageSrc = objectUrl;
        revoke = () => URL.revokeObjectURL(objectUrl);
      }
    } catch {
      /* fall through with original URL + crossOrigin loadImage */
    }
  }
  try {
    const image = await loadImage(imageSrc);
    const imgW = image.naturalWidth || 1;
    const imgH = image.naturalHeight || 1;
    const canvas = document.createElement("canvas");
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) throw new Error("Unable to export profile crop");

    ctx.clearRect(0, 0, outputSize, outputSize);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Match bannerImageFrameStyle: object-fit cover + object-position + scale.
    const cover = getCoverScale(imgW, imgH, outputSize, outputSize);
    const baseW = imgW * cover;
    const baseH = imgH * cover;
    const px = frame.x / 100;
    const py = frame.y / 100;
    const ox = outputSize * px;
    const oy = outputSize * py;
    ctx.translate(ox, oy);
    ctx.scale(frame.zoom, frame.zoom);
    ctx.translate(-ox, -oy);
    ctx.drawImage(
      image,
      (outputSize - baseW) * px,
      (outputSize - baseH) * py,
      baseW,
      baseH,
    );

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) resolve(result);
          else reject(new Error("Failed to export profile crop"));
        },
        "image/png",
      );
    });
    return new File(
      [blob],
      args.fileName || `lib-avatar-crop-${Date.now()}.png`,
      { type: "image/png" },
    );
  } finally {
    revoke?.();
  }
}

export function resolvedBannerCta(
  cfgCta: unknown,
  bannerCta: unknown,
  editedCta?: unknown,
): string {
  const edited = String(editedCta ?? "").trim();
  if (edited) return edited;
  const fromCfg = String(cfgCta ?? "").trim();
  if (fromCfg) return fromCfg;
  return String(bannerCta ?? "").trim();
}
